// ──────────────────────────────────────────────────────────────────────
// Tier Preset Suggestions — Phase 2B-1 (preview-only)
// Returns an array of suggested tier objects based on the user's
// guided-builder answers. DISPLAY ONLY — never submitted to the backend.
// ──────────────────────────────────────────────────────────────────────

// Internal helper: consistent shape for every suggestion card
function mk(name, priceLabel, attendeesPerPass, note) {
  return {
    name,
    priceLabel,
    attendeeCountLabel: `${attendeesPerPass} ${attendeesPerPass === 1 ? 'attendee' : 'attendees'} per pass`,
    note,
  };
}

// Normalize so the function tolerates snake_case keys too, just in case
function norm(v) {
  return (v || '').toString().trim();
}

export function getTierPresetSuggestions({ event_type, guest_type, pricing_style } = {}) {
  const etype = norm(event_type);
  const gtype = norm(guest_type);
  const pstyle = norm(pricing_style);

  // ── Free / Decide Later — single simple default ─────────────────────
  if (pstyle === 'Free' || pstyle === 'Decide Later') {
    return [mk('General Access', '$0 or set later', 1, 'Simple default access option')];
  }

  // ── Flat Rate — single flat-price default ───────────────────────────
  if (pstyle === 'Flat Rate') {
    return [mk('General Access', 'Set flat price later', 1, 'Single price for all guests')];
  }

  // ── Tiered — branch by event_type first, then guest_type ────────────
  if (pstyle === 'Tiered') {
    // Event-type overrides
    if (etype === 'Sporting Event') {
      return [
        mk('General Admission', 'Set GA price later', 1, 'Standard entry'),
        mk('VIP Seating', 'Set VIP price later', 1, 'Premium seats and perks'),
        mk('Group Pass', 'Set group price later', 4, 'Good for families, teams, or groups'),
      ];
    }
    if (etype === 'Club / Venue') {
      return [
        mk('Early Bird', 'Set early-bird price later', 1, 'Discounted entry for early buyers'),
        mk('General Admission', 'Set GA price later', 1, 'Standard entry'),
        mk('VIP Access', 'Set VIP price later', 1, 'Premium access with perks'),
      ];
    }

    // Default tiered presets by guest_type
    if (gtype === 'Couples Only') {
      return [mk('Couples Access', 'Set couple price later', 2, 'Best for couple-based entry')];
    }
    if (gtype === 'Singles + Couples') {
      return [
        mk('Couples Access', 'Set couple price later', 2, 'Best for paired guests'),
        mk('Single Female Access', 'Set single price later', 1, 'Optional single guest tier'),
        mk('Single Male Access', 'Set single price later', 1, 'Optional single guest tier'),
      ];
    }
    if (gtype === 'General Admission') {
      return [
        mk('General Admission', 'Set GA price later', 1, 'Standard entry'),
        mk('VIP Access', 'Set VIP price later', 1, 'Optional premium tier'),
      ];
    }
    if (gtype === 'Invite Only') {
      return [mk('Invite Access', 'Set price later', 1, 'Best for invite-based entry')];
    }
    // guest_type = Custom (or anything else)
    return [mk('General Access', 'Set price later', 1, 'Editable starter tier')];
  }

  // ── No pricing selected yet — safe fallback ─────────────────────────
  return [mk('General Access', 'Set price later', 1, 'Editable starter tier')];
}
